"use client";

import { Box, Button, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { IframeDialog } from "@/_components/ui/IframeDialog";
import { useFaqAccordion } from "../../_hooks/useFaqAccordion";
import { ScrollReveal } from "./ScrollReveal";
import { SectionLabel } from "./SectionLabel";

export function LandingFaqRedesign() {
  const t = useTranslations("landing.faq");
  const tRedesign = useTranslations("landing.redesign.faq_sidebar");
  const tCta = useTranslations("landing.cta");
  const questions = t.raw("questions") as Array<{
    question: string;
    answer: string;
  }>;
  const firstSix = questions.slice(0, 6);
  const { toggle, isOpen } = useFaqAccordion(null);

  return (
    <Box
      as="section"
      id="faq"
      aria-labelledby="landing-faq-heading"
      py={{ base: 16, md: 36 }}
      px={{ base: 4, md: 0 }}
      bg="white"
    >
      <Box maxW="1100px" mx="auto">
        <Grid
          templateColumns={{ base: "1fr", lg: "1fr 2fr" }}
          gap={{ base: 10, lg: 20 }}
          alignItems="start"
        >
          <GridItem position={{ lg: "sticky" }} top={{ lg: "80px" }}>
            <ScrollReveal>
              <SectionLabel>{tRedesign("badge")}</SectionLabel>
              <Text
                as="h2"
                id="landing-faq-heading"
                fontFamily="heading"
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="semibold"
                letterSpacing="-1px"
                color="neutral.900"
                lineHeight={1.1}
              >
                {tRedesign("heading")}
              </Text>
              <Text
                textStyle="xs"
                color="neutral.500"
                lineHeight={1.7}
                mt={3.5}
                maxW="md"
              >
                {tRedesign("subtitle")}
              </Text>
              <IframeDialog
                title={tCta("book_demo")}
                trigger={
                  <Button
                    mt={7}
                    colorPalette="primary"
                    borderRadius="full"
                    textStyle="xs"
                    py={3}
                    px={5}
                  >
                    {tCta("book_demo")} →
                  </Button>
                }
                iframeHeight={{ base: "500px", md: "700px" }}
                variant="demo"
              />
            </ScrollReveal>
          </GridItem>

          <ScrollReveal delay={0.15}>
            <Box as="ul" listStyleType="none" m={0} p={0}>
              {firstSix.map((item, index) => (
                <Box
                  as="li"
                  key={item.question}
                  borderBottom="1px solid"
                  borderColor="neutral.200"
                  overflow="hidden"
                  minW={0}
                >
                  <Button
                    variant="ghost"
                    w="full"
                    justifyContent="space-between"
                    h="auto"
                    py={5}
                    px={0}
                    fontWeight="500"
                    color="neutral.900"
                    borderRadius={0}
                    _hover={{ color: "primary.500", bg: "transparent" }}
                    onClick={() => toggle(index)}
                    gap={4}
                    bg="transparent"
                    minW={0}
                    overflow="hidden"
                  >
                    <Text
                      as="span"
                      textAlign="left"
                      fontFamily="heading"
                      textStyle="sm"
                      flex={1}
                      minW={0}
                      whiteSpace="normal"
                      wordBreak="break-word"
                      overflowWrap="anywhere"
                    >
                      {item.question}
                    </Text>
                    <Flex
                      w="24px"
                      h="24px"
                      borderRadius="full"
                      border="1px solid"
                      align="center"
                      justify="center"
                      textStyle="sm"
                      flexShrink={0}
                      transition="all 0.25s"
                      bg={isOpen(index) ? "primary.500" : "transparent"}
                      color={isOpen(index) ? "white" : "neutral.500"}
                      borderColor={
                        isOpen(index) ? "primary.500" : "neutral.200"
                      }
                      transform={isOpen(index) ? "rotate(45deg)" : undefined}
                    >
                      +
                    </Flex>
                  </Button>
                  <Box
                    maxH={isOpen(index) ? "200px" : "0"}
                    overflow="hidden"
                    transition="max-height 0.35s ease, padding 0.35s ease"
                    pb={isOpen(index) ? 5 : 0}
                    minW={0}
                  >
                    <Text
                      textStyle="xs"
                      color="neutral.500"
                      lineHeight={1.7}
                      whiteSpace="normal"
                      wordBreak="break-word"
                      overflowWrap="anywhere"
                    >
                      {item.answer}
                    </Text>
                  </Box>
                </Box>
              ))}
            </Box>
          </ScrollReveal>
        </Grid>
      </Box>
    </Box>
  );
}
